'use client';

import { useEffect } from 'react';

import { sendGTMEvent } from '@/fo/api/gtm';

const VIDEO_EVENT_CATEGORY = 'Video';

const getVideoLabel = (video: HTMLVideoElement) => {
  return video.getAttribute('title') || '';
};

export function VideoTracking() {
  useEffect(() => {
    const playedVideos = new WeakSet<HTMLVideoElement>();
    const completedVideos = new WeakSet<HTMLVideoElement>();

    const handlePlay = (event: Event) => {
      const target = event.target;

      if (!(target instanceof HTMLVideoElement)) {
        return;
      }
      if (target.seeking) {
        return;
      }

      if (playedVideos.has(target)) {
        return;
      }

      playedVideos.add(target);

      const eventLabel = getVideoLabel(target);

      if (!eventLabel) {
        return;
      }

      sendGTMEvent({
        event: 'video_interaction',
        eventCategory: VIDEO_EVENT_CATEGORY,
        eventAction: 'Play',
        eventLabel
      });
    };

    const handlePause = (event: Event) => {
      const target = event.target;

      if (!(target instanceof HTMLVideoElement)) {
        return;
      }
      if (target.seeking) {
        return;
      }

      const { duration, currentTime } = target;
      const isNearEnd =
        Number.isFinite(duration) &&
        duration > 0 &&
        duration - currentTime <= 0.5;

      if (isNearEnd) {
        return;
      }

      const eventLabel = getVideoLabel(target);

      if (!eventLabel) {
        return;
      }

      sendGTMEvent({
        event: 'video_interaction',
        eventCategory: VIDEO_EVENT_CATEGORY,
        eventAction: 'Pause',
        eventLabel
      });

      playedVideos.delete(target);
    };

    const handleEnded = (event: Event) => {
      const target = event.target;

      if (!(target instanceof HTMLVideoElement)) {
        return;
      }

      if (completedVideos.has(target)) {
        return;
      }

      const eventLabel = getVideoLabel(target);

      if (!eventLabel) {
        return;
      }

      sendGTMEvent({
        event: 'video_interaction',
        eventCategory: VIDEO_EVENT_CATEGORY,
        eventAction: 'Complete',
        eventLabel
      });

      playedVideos.delete(target);
      completedVideos.add(target);
    };

    const handleTimeUpdate = (event: Event) => {
      const target = event.target;

      if (!(target instanceof HTMLVideoElement)) {
        return;
      }

      // Фоллбек только для зацикленных видео, где ended может не стрелять стабильно
      if (!target.loop || completedVideos.has(target) || target.seeking) {
        return;
      }

      const { duration, currentTime } = target;
      const isNearEnd =
        Number.isFinite(duration) &&
        duration > 0 &&
        duration - currentTime <= 0.5;

      if (!isNearEnd) {
        return;
      }

      const eventLabel = getVideoLabel(target);

      if (!eventLabel) {
        return;
      }

      sendGTMEvent({
        event: 'video_interaction',
        eventCategory: VIDEO_EVENT_CATEGORY,
        eventAction: 'Complete',
        eventLabel
      });

      playedVideos.delete(target);
      completedVideos.add(target);
    };

    document.addEventListener('play', handlePlay, true);
    document.addEventListener('pause', handlePause, true);
    document.addEventListener('ended', handleEnded, true);
    document.addEventListener('timeupdate', handleTimeUpdate, true);

    return () => {
      document.removeEventListener('play', handlePlay, true);
      document.removeEventListener('pause', handlePause, true);
      document.removeEventListener('ended', handleEnded, true);
      document.removeEventListener('timeupdate', handleTimeUpdate, true);
    };
  }, []);

  return null;
}
